/**
 * 🟦 V11.x Refactor P1 — Panel de Recetas (extraído de LyonHeaderButton.jsx).
 *
 * 🟦 V13.3 — Headers Colapsables (reemplaza el toggle binario V13.2).
 * Dos secciones independientes y persistidas en localStorage:
 *   - "Marketplace (N)"  → recetas con id `mkt:*` (curadas por DamyxIA)
 *   - "Mis recetas (N)"  → recetas custom del usuario
 *
 * Componente PURO. Recibe estado y handlers via props. Sin lógica propia.
 * Preserva los testids del inline original:
 *  - lyon-recipes-panel
 *  - lyon-close-recipes-panel
 *  - lyon-recipe-item-{id}
 *  - lyon-apply-recipe-{id}
 *  - lyon-export-recipe-{id}
 *  - lyon-share-recipe-{id}
 *  - lyon-delete-recipe-{id}
 *  - lyon-recipes-section-marketplace-toggle
 *  - lyon-recipes-section-custom-toggle
 *  - lyon-recipes-section-marketplace
 *  - lyon-recipes-section-custom
 */
import { useMemo, useState } from 'react';
import {
  X, Loader2, BookOpen, Wand2, AlertTriangle, CheckCircle2,
  Trash2, Download, Share2, ChevronDown, ChevronRight, Store, User, Send,
} from 'lucide-react';
import ProposeRecipeModal from './ProposeRecipeModal';
import { isUserPremium, showPremiumGateMessage } from '../../genio/premiumGate';

const SECTION_MKT_KEY = 'damyxia.lyon.recipes.section.mkt.collapsed';
const SECTION_CUSTOM_KEY = 'damyxia.lyon.recipes.section.custom.collapsed';

const readBool = (k, fallback = false) => {
  try { return localStorage.getItem(k) === '1'; } catch { return fallback; }
};
const writeBool = (k, v) => {
  try { localStorage.setItem(k, v ? '1' : '0'); } catch { /* ignore */ }
};

const isMarketplaceRecipe = (r) => String(r?.id || '').startsWith('mkt:');

const LyonRecipesPanel = ({
  open,
  onClose,
  recipes,
  recipeApplyState,
  onApply,
  onExport,
  onShare,
  onDelete,
  es,
}) => {
  // 🟦 V13.3 — Estado de colapso por sección, persistido en localStorage.
  const [mktCollapsed, setMktCollapsed] = useState(() => readBool(SECTION_MKT_KEY, false));
  const [customCollapsed, setCustomCollapsed] = useState(() => readBool(SECTION_CUSTOM_KEY, false));
  // 🟦 V14.1.1 — Modal "Proponer al Marketplace" (solo aparece para custom recipes).
  const [proposeFor, setProposeFor] = useState(null);

  const toggleMkt = () => {
    setMktCollapsed((prev) => {
      const next = !prev;
      writeBool(SECTION_MKT_KEY, next);
      return next;
    });
  };
  const toggleCustom = () => {
    setCustomCollapsed((prev) => {
      const next = !prev;
      writeBool(SECTION_CUSTOM_KEY, next);
      return next;
    });
  };

  const { marketplaceRecipes, customRecipes } = useMemo(() => {
    const mkt = [];
    const own = [];
    for (const r of recipes) {
      if (isMarketplaceRecipe(r)) mkt.push(r);
      else own.push(r);
    }
    return { marketplaceRecipes: mkt, customRecipes: own };
  }, [recipes]);

  if (!open) return null;

  const renderRecipeCard = (r, canPropose = false) => {
    const st = recipeApplyState[r.id];
    return (
      <div
        key={r.id}
        data-testid={`lyon-recipe-item-${r.id}`}
        className="border border-slate-700/60 rounded-lg bg-slate-900/60 p-3 hover:border-fuchsia-500/50 transition-colors"
      >
        <div className="flex items-start gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{r.name}</p>
            <p className="text-[10px] text-slate-400 font-mono">
              &lt;{r.nodeType}&gt; · {r.steps.length} {es ? 'pasos' : 'steps'} · {new Date(r.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              // 🟥 V18.0 — Premium Gate: aplicar receta MODIFICA el proyecto.
              if (!isUserPremium()) { showPremiumGateMessage(); return; }
              onApply(r);
            }}
            disabled={st === 'loading'}
            data-testid={`lyon-apply-recipe-${r.id}`}
            className={`flex-1 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors flex items-center justify-center gap-1.5 ${
              st === 'loading' ? 'bg-fuchsia-900/40 text-fuchsia-200 cursor-wait' :
              st === 'ok' ? 'bg-emerald-900/40 text-emerald-200' :
              st === 'err' ? 'bg-red-900/40 text-red-200' :
              'bg-fuchsia-600 hover:bg-fuchsia-500 text-white'
            }`}
          >
            {st === 'loading' ? <><Loader2 className="w-3 h-3 animate-spin" /> {es ? 'Aplicando' : 'Applying'}</> :
             st === 'ok' ? <><CheckCircle2 className="w-3 h-3" /> {es ? 'Aplicado' : 'Applied'}</> :
             st === 'err' ? <><AlertTriangle className="w-3 h-3" /> {es ? 'Error' : 'Error'}</> :
             <><Wand2 className="w-3 h-3" /> {es ? 'Aplicar a nodo seleccionado' : 'Apply to selected node'}</>}
          </button>
          {canPropose && (
            <button
              type="button"
              onClick={() => setProposeFor(r)}
              data-testid={`lyon-propose-recipe-${r.id}`}
              title={es ? 'Proponer al Marketplace' : 'Propose to Marketplace'}
              className="p-1.5 rounded-md bg-slate-800 hover:bg-cyan-700 text-cyan-300 hover:text-white transition-colors"
            >
              <Send className="w-3 h-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onExport(r)}
            data-testid={`lyon-export-recipe-${r.id}`}
            title={es ? 'Exportar como archivo JSON' : 'Export as JSON file'}
            className="p-1.5 rounded-md bg-slate-800 hover:bg-emerald-900/60 text-slate-400 hover:text-emerald-200 transition-colors"
          >
            <Download className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onShare(r)}
            data-testid={`lyon-share-recipe-${r.id}`}
            title={es ? 'Compartir por URL' : 'Share by URL'}
            className="p-1.5 rounded-md bg-slate-800 hover:bg-cyan-900/60 text-slate-400 hover:text-cyan-200 transition-colors"
          >
            <Share2 className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(r.id)}
            data-testid={`lyon-delete-recipe-${r.id}`}
            title={es ? 'Eliminar receta' : 'Delete recipe'}
            className="p-1.5 rounded-md bg-slate-800 hover:bg-red-900/60 text-slate-400 hover:text-red-200 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="lyon-recipes-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-slate-950 border border-slate-700/70 rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-fuchsia-950/40 to-purple-950/40">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-fuchsia-300" /> {es ? 'Tus recetas' : 'Your recipes'}
            <span className="text-[10px] font-mono text-slate-400">({recipes.length})</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="lyon-close-recipes-panel"
            className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
            title={es ? 'Cerrar (Esc)' : 'Close (Esc)'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* ─── Sección Marketplace ─── */}
          <section data-testid="lyon-recipes-section-marketplace" className="rounded-lg border border-cyan-500/20 bg-cyan-950/10">
            <button
              type="button"
              onClick={toggleMkt}
              data-testid="lyon-recipes-section-marketplace-toggle"
              aria-expanded={!mktCollapsed}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-cyan-900/20 transition-colors rounded-t-lg"
            >
              <span className="flex items-center gap-2 text-[12px] font-bold text-cyan-200">
                {mktCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <Store className="w-3.5 h-3.5" />
                {es ? 'Marketplace' : 'Marketplace'}
                <span className="text-[10px] font-mono text-cyan-400/80">({marketplaceRecipes.length})</span>
              </span>
              <span className="text-[10px] text-cyan-400/70 font-mono">
                {es ? 'curadas por DamyxIA' : 'curated by DamyxIA'}
              </span>
            </button>
            {!mktCollapsed && (
              <div className="px-3 pb-3 pt-1 space-y-2">
                {marketplaceRecipes.length === 0 ? (
                  <div className="text-center py-4 text-slate-500 text-xs">
                    {es ? 'Las recetas curadas aparecen automáticamente al abrir el Builder.' : 'Curated recipes appear automatically when opening the Builder.'}
                  </div>
                ) : (
                  marketplaceRecipes.map((r) => renderRecipeCard(r, false))
                )}
              </div>
            )}
          </section>

          {/* ─── Sección Mis Recetas ─── */}
          <section data-testid="lyon-recipes-section-custom" className="rounded-lg border border-fuchsia-500/20 bg-fuchsia-950/10">
            <button
              type="button"
              onClick={toggleCustom}
              data-testid="lyon-recipes-section-custom-toggle"
              aria-expanded={!customCollapsed}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-fuchsia-900/20 transition-colors rounded-t-lg"
            >
              <span className="flex items-center gap-2 text-[12px] font-bold text-fuchsia-200">
                {customCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <User className="w-3.5 h-3.5" />
                {es ? 'Mis recetas' : 'My recipes'}
                <span className="text-[10px] font-mono text-fuchsia-400/80">({customRecipes.length})</span>
              </span>
              <span className="text-[10px] text-fuchsia-400/70 font-mono">
                {es ? 'guardadas por vos' : 'saved by you'}
              </span>
            </button>
            {!customCollapsed && (
              <div className="px-3 pb-3 pt-1 space-y-2">
                {customRecipes.length === 0 ? (
                  <div className="text-center py-6 text-slate-500 text-xs">
                    <p>
                      {es ? 'Aún no creaste recetas en esta app.' : "You haven't created custom recipes in this app yet."}
                    </p>
                    <p className="text-[11px] text-slate-600 mt-1">
                      {es ? 'Encadená 2+ comandos sobre un nodo y aparecerá el botón "Guardar receta".' : 'Chain 2+ commands on a node and the "Save recipe" button will appear.'}
                    </p>
                  </div>
                ) : (
                  customRecipes.map(renderRecipeCard)
                )}
              </div>
            )}
          </section>
        </div>
      </div>
      {/* 🟦 V14.1.1 — Modal Proponer al Marketplace */}
      <ProposeRecipeModal
        open={Boolean(proposeFor)}
        recipe={proposeFor}
        onClose={() => setProposeFor(null)}
      />
    </div>
  );
};

export default LyonRecipesPanel;
